const { pick, last, fromPairs, sortBy, get, flatten, isEmpty, keyBy } = require('lodash');
const { isURL, } = require('validator');
const { format: formatDate, } = require('date-fns');
const numeral = require('numeral');

const { plans, } = require('./company');
const { documentTypes, dimensions, manualTypes, } = require('../config');

const { keys, entries } = Object;
const displays = {
  periodComparisonBs: { label: '期間比較 BS', },
  periodComparisonPl: { label: '期間比較 PL', },
  periodComparisonCr: { label: '期間比較 CR', },
  monthlyTrendsBs: { label: '月次推移 BS', },
  monthlyTrendsPl: { label: '月次推移 PL', },
  monthlyTrendsCr: { label: '月次推移 CR', },

  // NOTE: 一旦、ここは以前のを利用する
  sectionsBudgetsPl: { label: '予実', },
  sectionBudgetsPl: { label: '予実月次推移', },
};
const expressionTokens = (expression) => {
  const tokens = expression?.match(/(((acc|cat|cus):"([^"]+)"(?:\:(累計値|発生値))?)|[+-/*()]|\d+)/g);
  const invalidTokens = (tokens || []).reduce((x, y) => x.replace(y, ''), expression || '').split(/\s+/).filter(_ => _);
  return { expression, tokens, invalidTokens };
};

const fields = ({ company, enabledBudgetSubjectTypes = {}, customAccountItems = [] } = {}) => {
  return {
    name: {
      type: 'string',
      label: '科目名',
      validations: {
        required: v => !isEmpty(v),
        unique: (v, s) => !s.conflicted && !customAccountItems.map(_ => _.name).includes(v),
      },
    },
    displays: {
      type: 'multiSelect',
      label: '表示画面',
      options: entries(displays).map(([k, v]) => ({ label: v.label, value: k })),
    },
    dimension: {
      type: 'select',
      label: '明細',
      options: entries(dimensions).map(([k, v]) => ({ label: v, value: k })),
      validations: {
        required: v => !isEmpty(v),
        unmatched: v => isEmpty(v) || keys(dimensions).includes(v),
      },
      initialValue: 'none',
    },
    isManual: {
      type: 'boolean',
      label: '手入力',
      initialValue: false,
    },
    expression: {
      type: 'text',
      label: '計算式',
      validations: {
        required: v => !isEmpty(v),
        validTokens: v => expressionTokens(v).invalidTokens.length === 0,
      },
      hidden: _ => _.isManual,
    },
    manualType: {
      type: 'select',
      label: '手入力数値タイプ',
      options: entries(manualTypes).map(([k, v]) => ({ label: v, value: k })),
      validations: {
        required: v => !isEmpty(v),
        unmatched: v => isEmpty(v) || keys(manualTypes).includes(v),
      },
      initialValue: 'occurrence',
      hidden: _ => !_.isManual,
    },
    displayExpression: {
      type: 'string',
      label: '表示形式',
      initialValue: '0,0',
      validations: {
        required: v => !isEmpty(v),
      },
    },
    ...entries(enabledBudgetSubjectTypes).reduce((x, [k, { label }]) => {
      return {
        ...x,
        [`${k}__budget__url`]: {
          type: 'string',
          label: `${label} 予算 スプレッドシートURL`,
          validations: {
            format: v => isEmpty(v) || /\/spreadsheets\/d\/[^/]+/.test(v || '') || isURL(v || ''),
          },
          hidden: _ => !_.isManual,
        },
        [`${k}__performance__url`]: {
          type: 'string',
          label: `${label} 実績 スプレッドシートURL`,
          validations: {
            format: v => isEmpty(v) || /\/spreadsheets\/d\/[^/]+/.test(v || '') || isURL(v || ''),
          },
          hidden: _ => !_.isManual,
        },
      };
    }, {}),
  };
};

const computeRelatedItems = (customAccountItem, customAccountItemsByName, visited = new Set()) => {
  const { name, isManual, expression } = customAccountItem || {};
  if(isManual || !expression) return [];
  if(name && visited.has(name)) return [];
  const nextVisited = name ? new Set([...visited, name]) : visited;

  return [
    ...(expression.match(/acc:"([^"]+)"/g) || [])
      .map(_ => _.match(/acc:"([^"]+)"/)[1])
      .map(_ => _.split(':')[0])
      .map(_ => ({ type: 'accountItem', value: _ })),
    ...(expression.match(/cat:"([^"]+)"/g) || [])
      .map(_ => _.match(/cat:"([^"]+)"/)[1])
      .map(_ => ({ type: 'accountCategory', value: _ })),
    ...flatten((expression.match(/cus:"([^"]+)"/g) || [])
      .map(_ => _.match(/cus:"([^"]+)"/)[1])
      .map(_ => computeRelatedItems(customAccountItemsByName[_], customAccountItemsByName, nextVisited)))
  ];
};

const customAccountItemDependencyNames = (customAccountItem) => {
  const { isManual, expression } = customAccountItem || {};
  if(isManual || !expression) return [];
  return (expression.match(/cus:"([^"]+)"/g) || []).map(_ => _.match(/cus:"([^"]+)"/)[1]);
};

const findCustomAccountItemCycles = (customAccountItems) => {
  const itemsByName = keyBy(customAccountItems, 'name');
  const cycles = [];
  const seenCycleKeys = new Set();
  const dfs = (name, stack, onStack) => {
    const item = itemsByName[name];
    if(!item) return;
    customAccountItemDependencyNames(item).forEach((depName) => {
      if(onStack.has(depName)) {
        const cycleNodes = stack.slice(stack.indexOf(depName));
        // NOTE: 循環は最小ノード名から並び替えてキー化し、開始点違いの重複を取り除く。
        const minIndex = cycleNodes.indexOf([...cycleNodes].sort()[0]);
        const normalized = [...cycleNodes.slice(minIndex), ...cycleNodes.slice(0, minIndex)];
        const key = normalized.join('->');
        if(!seenCycleKeys.has(key)) {
          seenCycleKeys.add(key);
          cycles.push([...normalized, normalized[0]]);
        }
        return;
      }
      if(stack.includes(depName)) return;
      dfs(depName, [...stack, depName], new Set([...onStack, depName]));
    });
  };
  customAccountItems.forEach((item) => {
    dfs(item.name, [item.name], new Set([item.name]));
  });
  return cycles;
};

const manualValueSuffixes = { performance: '', budget: '_budget', forecast: '_forecast' };

// NOTE: 手入力カスタム科目の `<budgetSubjectType>__<budget|performance>__url` から Google Sheets API で読むシートを特定する。
// 「ウェブに公開」の CSV URL (/pub?) は fetch で直接読むため対象外として null を返す。
// fetchGoogleSheet はこの結果を登録済みシートの照合にも使うので、フロントとバックで判定を揃えること。
const googleSheetOfUrl = (url) => {
  if(url == null || /\/pub\?/.test(url)) return null;

  const match = url.match(/\/spreadsheets\/d\/([^/]+).*[?#&]gid=(\d+)/);
  return match && { spreadsheetId: match[1], sheetId: match[2] };
};

// NOTE: カスタム科目 1 件から、登録済みのスプレッドシート URL だけを配列で取り出す。
// URL のキーは fields() で `${budgetSubjectType}__budget__url` / `${budgetSubjectType}__performance__url` として
// 事業所の区分(section / segment1〜3 等)ごとに動的に作られるため、キー名を列挙せず末尾のパターンで拾う。
// 例: { name: '従業員数', section__budget__url: 'https://.../d/AAA/edit#gid=0', segment1__budget__url: '' }
//     → ['https://.../d/AAA/edit#gid=0'] (未入力の空文字は除外)
const manualValueUrlsOf = (customAccountItem) => {
  return entries(customAccountItem || {}).filter(([k, v]) => /__(budget|performance)__url$/.test(k) && v).map(([, v]) => v);
};

// NOTE: 手入力カスタム科目の月次値(occurrences)から累計値(balances)を再計算する。
// キーは `YYYY/MM` + `budgetSubjectId` + suffix('' | '_budget' | '_forecast') 。
// slice(7)は budgetSubjectId+suffix を返すため、実績/予算/見込は別系列として独立に積み上がる。
const computeBalances = (customAccountItem, monthValues, firstMonth, lastMonth) => {
  const monthValuesPairs = sortBy(entries(monthValues), '0');
  return fromPairs(
    monthValuesPairs.reduce((x, [k, v], i) => {
      const monthString = k.slice(0, 7);
      const budgetSubjectId = k.slice(7);
      const sameBudgetSubjectIdItems = x.filter(_ => _[0].slice(7) === budgetSubjectId);
      return [
        ...x,
        [k, (
          // NOTE: 今回の対象外は、もともとの値を使う
          (monthString < formatDate(firstMonth, 'YYYY/MM') || formatDate(lastMonth, 'YYYY/MM') < monthString) ? (
            get(customAccountItem, `balances.${k}`) || 0
          ) : (
            v + ((formatDate(firstMonth, 'YYYY/MM') < monthString && monthString <= formatDate(lastMonth, 'YYYY/MM')) ? ((last(sameBudgetSubjectIdItems) || {})[1] || 0) : 0)
          )
        )],
      ];
    }, [])
  );
};

const processManualCsvRows = async (customAccountItem, budgetSubjects, months, budgetPerformanceType, rows) => {
  const suffix = manualValueSuffixes[budgetPerformanceType];
  const [firstMonth] = months;
  const [lastMonth] = months.slice(-1);
  const addedMonthValues = months.reduce((x, month) => {
    const monthString = formatDate(month, 'YYYY/MM');
    const csvMonthString = formatDate(month, 'YYYYMM');
    const row = rows.find(_ => _['年月'] === csvMonthString);
    return {
      ...x,
      ...[...(budgetPerformanceType === 'performance' ? [{ id: '', name: '全社' }] : []), ...budgetSubjects].reduce((x, budgetSubject) => {
        return {
          ...x,
          [[monthString, budgetSubject.id, suffix].join('')]: numeral(get(row, [budgetSubject.name], 0), 10).value() || 0, // NOTE: ""だとgetで初期値使われない
        };
      }, {}),
    };
  }, {});
  const monthValues = {
    ...customAccountItem.values,
    ...addedMonthValues,
  };

  const balances = computeBalances(customAccountItem, monthValues, firstMonth, lastMonth);
  const occurrences = monthValues;
  await customAccountItem.ref.update({
    values: monthValues,
    balances,
    occurrences,
  });
  return rows;
};

// 見込CSV(科目 × _YYYYMM列)の1科目分を、手入力カスタム科目の _forecast 系列として保存する。
// addedMonthValues: { 'YYYY/MM<budgetSubjectId>_forecast': amount } 形式
const saveManualForecastValues = async (customAccountItem, addedMonthValues, months) => {
  const [firstMonth] = months;
  const [lastMonth] = months.slice(-1);
  const monthValues = {
    ...customAccountItem.values,
    ...addedMonthValues,
  };
  const balances = computeBalances(customAccountItem, monthValues, firstMonth, lastMonth);
  await customAccountItem.ref.update({
    values: monthValues,
    balances,
    occurrences: monthValues,
  });
};

module.exports = {
  fields,
  computeRelatedItems,
  customAccountItemDependencyNames,
  findCustomAccountItemCycles,
  displays,
  expressionTokens,
  processManualCsvRows,
  manualValueSuffixes,
  saveManualForecastValues,
  googleSheetOfUrl,
  manualValueUrlsOf,
};
